import { InjectionToken } from '@angular/core';

/**
 * Branding configuration interface
 * Defines app-specific branding assets and metadata
 */
export interface ZopBrandingConfig {
  /**
   * Application name displayed in UI
   */
  appName: string;

  /**
   * Full LIGHT-COLORED logo (with app name/text)
   * Shown on DARK backgrounds for contrast
   * Should be a relative path like 'assets/images/logo/app-full-light.svg'
   * Falls back to logoLightUrl if not provided
   */
  logoFullLightUrl?: string;

  /**
   * Full DARK-COLORED logo (with app name/text)
   * Shown on LIGHT backgrounds for contrast
   * Should be a relative path like 'assets/images/logo/app-full-dark.svg'
   * Falls back to logoDarkUrl if not provided
   */
  logoFullDarkUrl?: string;

  /**
   * Square/compact LIGHT-COLORED logo (icon only, no text)
   * Shown on DARK backgrounds for contrast
   * Should be a relative path like 'assets/images/logo/app-square-light.svg'
   * Falls back to logoLightUrl if not provided
   */
  logoSquareLightUrl?: string;

  /**
   * Square/compact DARK-COLORED logo (icon only, no text)
   * Shown on LIGHT backgrounds for contrast
   * Should be a relative path like 'assets/images/logo/app-square-dark.svg'
   * Falls back to logoDarkUrl if not provided
   */
  logoSquareDarkUrl?: string;

  /**
   * Default LIGHT-COLORED logo
   * Shown on DARK backgrounds for contrast
   * Used as fallback when specific variant is not provided
   * Should be a relative path like 'assets/images/logo/app-light.svg'
   */
  logoLightUrl: string;

  /**
   * Default DARK-COLORED logo
   * Shown on LIGHT backgrounds for contrast
   * Used as fallback when specific variant is not provided
   * Should be a relative path like 'assets/images/logo/app-dark.svg'
   */
  logoDarkUrl: string;

  /**
   * Alt text for logo images
   * Used for accessibility
   */
  logoAlt: string;

  /**
   * Favicon URL (optional)
   * Should be a relative path like 'assets/images/favicon.ico'
   */
  faviconUrl?: string;

  /**
   * Additional app-specific images
   * Key-value pairs for named images that can be referenced across components
   *
   * @example
   * ```typescript
   * images: {
   *   'authHero': 'assets/images/auth/hero.svg',
   *   'authBackground': 'assets/images/auth/background.jpg',
   *   'emptyState': 'assets/images/empty-state.svg'
   * }
   * ```
   */
  images?: Record<string, string>;

  /**
   * Lottie animation URLs for various use cases
   * Key-value pairs for named animations
   *
   * @example
   * ```typescript
   * animations: {
   *   'authHero': 'assets/animations/auth-hero.json',
   *   'loading': 'assets/animations/loading.json'
   * }
   * ```
   */
  animations?: Record<string, string>;

  /**
   * Theme colors for navigation and UI elements
   * Optional configuration for app-specific color branding
   *
   * @example
   * ```typescript
   * themeColors: {
   *   sidebarBgColor: '#19274d',         // Sidebar background color
   *   sidebarTextColor: '#ffffff',       // Sidebar text color
   *   sidebarNavColor: '#4a90e2'         // Nav accent/active color (optional)
   * }
   * ```
   */
  themeColors?: {
    /**
     * Sidebar background color
     * Used in light mode or as fallback if dark mode color not specified
     * @default undefined (falls back to bg-white dark:bg-gray-900)
     */
    sidebarBgColor?: string;

    /**
     * Sidebar background color for dark mode
     * Optional - if not provided, sidebarBgColor will be used for both modes
     */
    sidebarBgColorDark?: string;

    /**
     * Sidebar text color
     * Used in light mode or as fallback if dark mode color not specified
     * @default undefined (falls back to text-gray-900 dark:text-white)
     */
    sidebarTextColor?: string;

    /**
     * Sidebar text color for dark mode
     * Optional - if not provided, sidebarTextColor will be used for both modes
     */
    sidebarTextColorDark?: string;

    /**
     * Primary color for sidebar navigation elements (group titles, active states)
     * Optional - if not provided, will use sidebarTextColor or fallback to Tailwind primary colors
     */
    sidebarNavColor?: string;

    /**
     * Primary color for sidebar navigation in dark mode
     * Optional - if not provided, sidebarNavColor will be used for both modes
     */
    sidebarNavColorDark?: string;

    /**
     * CSS gradient string for sidebar background
     * Takes precedence over sidebarBgColor when provided
     * Accepts any valid CSS gradient, e.g. 'linear-gradient(to bottom, #FE4F30 10%, #E12A6D 70%)'
     * Optional - if not provided, sidebarBgColor is used
     */
    sidebarBgGradient?: string;

    /**
     * CSS gradient string for sidebar background in dark mode
     * Optional - if not provided, sidebarBgGradient will be used for both modes
     */
    sidebarBgGradientDark?: string;

    /**
     * Whether the gradient background is visually dark (used to pick logo contrast)
     * Set to true if the gradient is dark so a light logo is used; false for a light gradient
     * Optional - defaults to true (dark gradient assumed)
     */
    sidebarBgGradientIsDark?: boolean;
  };
}

/**
 * Default branding configuration
 * Used as fallback when app doesn't provide configuration
 */
export const DEFAULT_BRANDING_CONFIG: ZopBrandingConfig = {
  appName: 'Application',
  logoLightUrl: 'assets/images/logo/default-light.svg',
  logoDarkUrl: 'assets/images/logo/default-dark.svg',
  logoAlt: 'Application Logo',
  images: {},
  animations: {},
};

/**
 * Injection token for branding configuration
 * Apps should provide their config using this token
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * {
 *   provide: ZOP_BRANDING_CONFIG,
 *   useValue: {
 *     appName: 'Zopup',
 *     logoLightUrl: 'assets/images/logo/zopup-light.svg',
 *     logoDarkUrl: 'assets/images/logo/zopup-dark.svg',
 *     logoAlt: 'Zopup Logo',
 *     images: {
 *       authHero: 'assets/images/auth/hero.svg'
 *     },
 *     animations: {
 *       authHero: 'assets/animations/zopup-dark.json'
 *     }
 *   }
 * }
 * ```
 */
export const ZOP_BRANDING_CONFIG = new InjectionToken<ZopBrandingConfig>(
  'ZOP_BRANDING_CONFIG',
  {
    providedIn: 'root',
    factory: () => DEFAULT_BRANDING_CONFIG,
  }
);
